import React from 'react';
import { ArrowRight, ClipboardList } from 'lucide-react';
import { LocationOpenTasksDto } from '../../../../../types/locationOverview';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { StatusBadge } from '../../../../common/StatusBadge';
import { formatDate, formatQty } from '../../item-card/format';
import { SectionCard } from '../../item-card/sections/SectionCard';

interface LocationOpenTasksSectionProps {
  tasks: LocationOpenTasksDto;
  binCode: string;
}

// StatusCodes-typer (se StatusCodes_Oversikt.md) — visningsnavn hentes via StatusBadge
const ST_LINE_STATUS = 1;
const ST_STC_TRANS_LINE_TYPE = 3;
const ST_INT_RELOCATION_TRANS_TYPE = 25;

const Row: React.FC<{ badge: React.ReactNode; itemNo?: string | null; description?: string | null; sub?: React.ReactNode; qty: number }> =
  ({ badge, itemNo, description, sub, qty }) => (
    <li className="flex items-center gap-3 py-2.5">
      <div className="flex-shrink-0">{badge}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-900 font-mono break-words">{itemNo || '-'}</div>
        {description && <div className="text-xs text-gray-600 break-words">{description}</div>}
        {sub && <div className="text-xs text-gray-500 mt-0.5 break-words">{sub}</div>}
      </div>
      <div className="flex-shrink-0 text-sm font-semibold text-gray-900">{formatQty(qty)}</div>
    </li>
  );

/** Fra → til, med denne lokasjonen uthevet så retningen synes med en gang. */
const FromTo: React.FC<{ from?: string | null; to?: string | null; binCode: string }> = ({ from, to, binCode }) => {
  const isHere = (v?: string | null) => !!v && v.toLowerCase() === binCode.toLowerCase();
  const part = (v?: string | null) => <span className={isHere(v) ? 'font-semibold text-gray-800' : ''}>{v || '-'}</span>;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap font-mono">
      {part(from)}
      <ArrowRight className="h-3 w-3 text-gray-400" />
      {part(to)}
    </span>
  );
};

/**
 * Ventende operasjoner som berører lokasjonen: plukk herfra (PListLine), utlegg/påfylling fra eller
 * til (StcIntRelOrderLine), mottatt-venter-på-utlegg (StcIntRelocation) og telling/korreksjon/flytt (StcTrans).
 */
export const LocationOpenTasksSection: React.FC<LocationOpenTasksSectionProps> = ({ tasks, binCode }) => {
  const { t } = useTranslation();
  const prefix = 'warehouse.modules.locationLookup.tasks';
  const total = tasks.picking.length + tasks.putAwayReplenishment.length
    + tasks.awaitingPutAway.length + tasks.pendingTransactions.length;

  const groupTitle = (text: string) => (
    <h3 className="text-xs font-medium text-gray-500 uppercase mt-3 first:mt-0">{text}</h3>
  );

  return (
    <SectionCard icon={<ClipboardList className="h-4 w-4" />} title={t(`${prefix}.title`, {}, 'Åpne oppgaver')} count={total}>
      {total === 0 ? (
        <p className="text-sm text-gray-500 py-2">{t(`${prefix}.empty`, {}, 'Ingen åpne oppgaver på lokasjonen')}</p>
      ) : (
        <div>
          {tasks.picking.length > 0 && (
            <>
              {groupTitle(t(`${prefix}.picking`, {}, 'Plukk herfra'))}
              <ul className="divide-y divide-gray-100">
                {tasks.picking.map(p => (
                  <Row
                    key={`pl-${p.pListID}-${p.pListLineNo}`}
                    badge={<span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">PL {p.pListID}</span>}
                    itemNo={p.itemNo}
                    description={p.itemDescription}
                    sub={[p.orderNoERP && p.orderNoERP !== '0' ? p.orderNoERP : p.orderNo, p.operatorID, p.deliveryDate ? formatDate(p.deliveryDate) : null]
                      .filter(Boolean).join(' · ')}
                    qty={p.qtyRemaining}
                  />
                ))}
              </ul>
            </>
          )}

          {tasks.putAwayReplenishment.length > 0 && (
            <>
              {groupTitle(t(`${prefix}.putAwayReplenishment`, {}, 'Utlegg og påfylling'))}
              <ul className="divide-y divide-gray-100">
                {tasks.putAwayReplenishment.map(r => (
                  <Row
                    key={`rel-${r.stcOrderNo}-${r.stcLineNo}`}
                    badge={r.transType != null
                      ? <StatusBadge value={r.transType} statusType={ST_INT_RELOCATION_TRANS_TYPE} />
                      : <span className="text-xs text-gray-400">-</span>}
                    itemNo={r.itemNo}
                    description={r.itemDescription}
                    sub={<span className="inline-flex items-center gap-2 flex-wrap">
                      <FromTo from={r.fromBinCode} to={r.toBinCode} binCode={binCode} />
                      {[`#${r.stcOrderNo}`, r.operatorID, r.batchNo || r.serialNo].filter(Boolean).join(' · ')}
                    </span>}
                    qty={r.quantity - r.quantityMoved}
                  />
                ))}
              </ul>
            </>
          )}

          {tasks.awaitingPutAway.length > 0 && (
            <>
              {groupTitle(t(`${prefix}.awaitingPutAway`, {}, 'Mottatt — venter på lokasjon'))}
              <ul className="divide-y divide-gray-100">
                {tasks.awaitingPutAway.map(r => (
                  <Row
                    key={`await-${r.relID}`}
                    badge={r.transType != null
                      ? <StatusBadge value={r.transType} statusType={ST_INT_RELOCATION_TRANS_TYPE} />
                      : <span className="text-xs text-gray-400">-</span>}
                    itemNo={r.itemNo}
                    description={r.itemDescription}
                    sub={[formatDate(r.transDt), r.batchNo || r.serialNo].filter(s => s && s !== '-').join(' · ')}
                    qty={r.quantity}
                  />
                ))}
              </ul>
            </>
          )}

          {tasks.pendingTransactions.length > 0 && (
            <>
              {groupTitle(t(`${prefix}.pendingTransactions`, {}, 'Telling, korreksjon og flytt'))}
              <ul className="divide-y divide-gray-100">
                {tasks.pendingTransactions.map(s => (
                  <Row
                    key={`st-${s.batchID}-${s.lineID}`}
                    badge={s.lineType != null
                      ? <StatusBadge value={s.lineType} statusType={ST_STC_TRANS_LINE_TYPE} />
                      : <span className="text-xs text-gray-400">-</span>}
                    itemNo={s.itemNo}
                    description={s.itemDescription}
                    sub={<span className="inline-flex items-center gap-2 flex-wrap">
                      {s.toLocationName && <FromTo from={s.locationName} to={s.toLocationName} binCode={binCode} />}
                      {s.lineStatus != null && <StatusBadge value={s.lineStatus} statusType={ST_LINE_STATUS} />}
                      {[s.operatorID, formatDate(s.transDt)].filter(v => v && v !== '-').join(' · ')}
                    </span>}
                    qty={s.quantity}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </SectionCard>
  );
};
